import { useEffect, useState, useMemo } from 'react';
import { warmVideoOrigins } from '@/services/connectionWarmService';
import ModeBar from '@/components/layout/ModeBar/ModeBar';
// Hero section (top video spotlight) is temporarily hidden from the main
// dashboard page — video now plays via the asset detail drawer flow instead.
// Uncomment to restore: HeroSection, VideoPlayer, TranscriptPanel.
// import HeroSection from '@/components/layout/HeroSection/HeroSection';
// import VideoPlayer from '@/components/player/VideoPlayer/VideoPlayer';
// import TranscriptPanel from '@/components/player/TranscriptPanel/TranscriptPanel';
import ContentRow from '@/components/content/ContentRow/ContentRow';
import MediaTile from '@/components/content/MediaTile/MediaTile';
import TrendingTile from '@/components/content/TrendingTile/TrendingTile';
import SearchResults from '@/components/search/SearchResults/SearchResults';
import MobileSearchBar from '@/components/ui/MobileSearchBar/MobileSearchBar';
import ShareModal from '@/components/ui/ShareModal/ShareModal';
import DownloadModal from '@/components/ui/DownloadModal/DownloadModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { setIntelligenceMode } from '@/features/ui/uiSlice';
import { fetchLibrary, deleteSection, removeAssetFromSection, deleteAsset } from '@/features/sections/sectionsSlice';
import { fetchHeroVideo, fetchStorageAsset } from '@/features/search/searchSlice';
import { trackEvent } from '@/services/analyticsService';
import { ANALYTICS_EVENTS } from '@/config/analyticsEvents';
// import { seekToSeconds, setPlaying, togglePlay } from '@/features/player/playerSlice'; // hero hidden
import type { ContentRow as ContentRowType, IntelligenceMode, MediaItem, TrendingItem } from '@/types';
// import { toDisplayText } from '@/utils/formatters'; // hero hidden
import styles from './Pages.module.css';

const DEFAULT_TITLE = 'The Real Value of AI — Smarthub Demo';
// const DEFAULT_DESC = '...'; // hero hidden — only used by the commented-out heroDesc below

const LAST_ASSET_KEY = 'last_hero_asset_id';

const INTELLIGENCE_OPTIONS: { value: IntelligenceMode; label: string; disabled?: boolean }[] = [
  { value: 'all', label: 'View all' },
  { value: 'video', label: 'Video Intelligence' },
  { value: 'image', label: 'Image Intelligence' },
  { value: 'document', label: 'Document Intelligence', disabled: true },
];

/** Collections that should always surface in image mode, even with 0 images. */
function isPhotosCollection(row: ContentRowType): boolean {
  return row.slug === 'photos' || /\bphotos?\b/i.test(row.title);
}

function countLabel(n: number, noun: string): string {
  return `${n} ${noun}${n !== 1 ? 's' : ''}`;
}

/**
 * Scope the dashboard collections to the active intelligence mode.
 * - all:   everything, unfiltered.
 * - video: keep every non-image item (videos/audio/other + untyped), drop rows
 *   left empty. Trending rows (video clips) are always kept.
 * - image: keep only image items, and only collections that have at least one
 *   image (plus the Photos collection, always). The subtitle count is
 *   recomputed from the filtered images so it matches what's shown.
 */
function filterRowsByIntelligence(rows: ContentRowType[], mode: IntelligenceMode): ContentRowType[] {
  if (mode === 'all') return rows;

  if (mode === 'image') {
    return rows
      .filter((row) => row.type !== 'trending')
      .map((row) => {
        const items = (row.items as MediaItem[]).filter((it) => it.contentType === 'image');
        return { ...row, items, subtitle: countLabel(items.length, 'image') };
      })
      .filter((row) => row.items.length > 0 || isPhotosCollection(row));
  }

  // video (default)
  return rows
    .map((row) => {
      if (row.type === 'trending') return row;
      return {
        ...row,
        items: (row.items as MediaItem[]).filter((it) => it.contentType !== 'image'),
      };
    })
    .filter((row) => row.items.length > 0);
}

export default function MediaIntelligencePage() {
  const dispatch = useAppDispatch();
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [activeChips, setActiveChips] = useState<string[]>(['all']);

  // Confirmation dialogs
  const [pendingDeleteSection, setPendingDeleteSection] = useState<{ id: string; label: string } | null>(null);
  const [pendingRemoveAsset, setPendingRemoveAsset] = useState<{ assetId: string; assetTitle: string; sectionId: string; sectionTitle: string } | null>(null);
  const [pendingDeleteAsset, setPendingDeleteAsset] = useState<{ assetId: string; assetTitle: string } | null>(null);

  function toggleChip(id: string) {
    if (id === 'all') {
      setActiveChips(['all']);
      return;
    }
    setActiveChips((prev) => {
      const withoutAll = prev.filter((c) => c !== 'all');
      if (withoutAll.includes(id)) {
        const next = withoutAll.filter((c) => c !== id);
        return next.length === 0 ? ['all'] : next;
      }
      return [...withoutAll, id];
    });
  }

  // const isPlaying = useAppSelector((s) => s.player.isPlaying); // hero hidden
  const { mediaRows } = useAppSelector((s) => s.content);
  const libraryLoading = useAppSelector((s) => s.sections.libraryLoading);
  const activeAsset = useAppSelector((s) => s.search.activeAsset);
  const intelligenceMode = useAppSelector((s) => s.ui.intelligenceMode);

  // Scope the collections to the active intelligence mode (video / image)
  // before building chips or applying the chip filter.
  const modeRows = useMemo(
    () => filterRowsByIntelligence(mediaRows, intelligenceMode),
    [mediaRows, intelligenceMode],
  );

  // Build chip list: system sections first, then custom collections
  const systemCategories = useMemo(() => [
    { id: 'all', label: 'All' },
    ...modeRows
      .filter((row) => !row.isUserCustom)
      .map((row) => ({ id: row.id, label: row.title })),
    ...modeRows
      .filter((row) => row.isUserCustom)
      .map((row) => ({ id: row.id, label: row.title, isCustom: true as const })),
  ], [modeRows]);

  const filteredRows = activeChips.includes('all')
    ? modeRows
    : modeRows.filter((row) => activeChips.includes(row.id));

  const handleIntelligenceSwitch = (value: IntelligenceMode) => {
    if (value === intelligenceMode) return;
    trackEvent(ANALYTICS_EVENTS.INTELLIGENCE_MODE_SWITCH, { mode: value });
    dispatch(setIntelligenceMode(value));
    setActiveChips(['all']);
  };

  // Fetch live tab feeds on mount — each falls back silently to mock on error
  useEffect(() => {
    if (!activeAsset) {
      // Restore last watched video; fall back to latest from API if none saved
      const savedId = localStorage.getItem(LAST_ASSET_KEY);
      if (savedId) {
        dispatch(fetchStorageAsset(savedId));
      } else {
        dispatch(fetchHeroVideo());
      }
    }
    dispatch(fetchLibrary());
  }, [dispatch]);

  // Persist the last active asset id so it survives page refresh
  useEffect(() => {
    if (activeAsset?.asset_id) {
      localStorage.setItem(LAST_ASSET_KEY, activeAsset.asset_id);
    }
  }, [activeAsset?.asset_id]);

  // Videos are intentionally NOT preloaded here. Each tile loads its preview
  // lazily on hover (see useHoverVideoPreview) so the CDN isn't hit for every
  // video on page load — only for the one the user actually hovers.
  //
  // To keep that first hover snappy, we preconnect to the CDN origin(s) as soon
  // as rows arrive: this warms DNS + TCP + TLS with ZERO video bytes, so the
  // hover fetch skips the cold-connection round-trip and starts almost instantly.
  useEffect(() => {
    const urls: (string | undefined)[] = [];
    for (const row of mediaRows) {
      for (const item of row.items as (MediaItem | TrendingItem)[]) {
        urls.push((item as MediaItem).storage_url);
      }
    }
    warmVideoOrigins(urls);
  }, [mediaRows]);

  // Derive hero content from active search asset, or fall back to defaults
  const heroTitle = activeAsset
    ? activeAsset.file_name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')
    : DEFAULT_TITLE;

  // Hero section hidden — heroDesc/heroBadge/heroTags/heroSpeakers/heroDuration/
  // heroMetaItems were only consumed by <HeroSection> below. Uncomment together.
  // const rawHeroDesc = activeAsset?.summary ?? activeAsset?.all_sections?.scenes?.[0] ?? null;
  // const heroDesc = rawHeroDesc != null ? toDisplayText(rawHeroDesc) : DEFAULT_DESC;
  // const heroBadge = activeAsset
  //   ? activeAsset.query
  //     ? `${activeAsset.source_system} · ${activeAsset.matched_segments.length} matched segment${activeAsset.matched_segments.length !== 1 ? 's' : ''}`
  //     : activeAsset.source_system.replace(/_/g, ' ')
  //   : 'Spotlight · Indexed 2m ago';
  // const heroTags: string[] = activeAsset
  //   ? (activeAsset.all_sections?.key_points?.length
  //       ? activeAsset.all_sections.key_points
  //       : activeAsset.all_sections?.scenes ?? []
  //     ).slice(0, 8).map(toDisplayText)
  //   : [];
  // const heroSpeakers = activeAsset?.all_sections?.ai_speakers?.map(toDisplayText).join(', ');
  // const heroDuration = (() => {
  //   const ms = activeAsset?.duration_ms;
  //   if (!ms) return null;
  //   const s = Math.round(ms / 1000);
  //   const h = Math.floor(s / 3600);
  //   const m = Math.floor((s % 3600) / 60);
  //   const sec = s % 60;
  //   const ss = String(sec).padStart(2, '0');
  //   return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
  // })();
  // const heroMetaItems = activeAsset
  //   ? [
  //       ...(heroDuration ? [heroDuration, '·'] : []),
  //       ...(heroSpeakers ? [heroSpeakers] : ['1 speaker']),
  //       '·',
  //       activeAsset.source_system.replace(/_/g, ' '),
  //       '·',
  //       `${(activeAsset.file_size_mb ?? 0).toFixed(1)} MB`,
  //     ]
  //   : ['0:33', '·', '1 speaker', '·', '✓ Rights cleared', '·', 'MP4 source', '·', 'Indexed in 0m 28s'];

  return (
    <div>
      {/* Search results panel — shown when query ≥ 3 chars */}
      <SearchResults />

      {/* Mobile-only search bar — hidden on desktop (Navbar hosts search there) */}
      <MobileSearchBar />

      {/* Intelligence filter — scopes the whole dashboard */}
      <div className={styles.intelChips} role="group" aria-label="Intelligence filter">
        {INTELLIGENCE_OPTIONS.map((opt) => {
          const active = intelligenceMode === opt.value;
          return (
            <button
              key={opt.value}
              aria-pressed={active}
              className={`${styles.intelChip} ${active ? styles.intelChipActive : ''} ${opt.disabled ? styles.intelChipDisabled : ''}`}
              disabled={opt.disabled}
              onClick={() => !opt.disabled && handleIntelligenceSwitch(opt.value)}
            >
              {opt.label}
              {opt.disabled && <span className={styles.comingSoonBadge}>Coming soon</span>}
            </button>
          );
        })}
      </div>

      {/*
      ── HERO SECTION — temporarily hidden from the main dashboard page ──
      Video now plays via the asset detail drawer flow (see MediaTile.openDetails
      and AssetDetailDrawer). Uncomment this block together with the imports
      and hero* variables above to restore the top video spotlight.
      <HeroSection
        badgeText={heroBadge}
        badgeVariant="green"
        title={heroTitle}
        player={<VideoPlayer />}
        sidePanel={<TranscriptPanel />}
        description={heroDesc}
        tags={heroTags}
        meta={{ items: heroMetaItems }}
        actions={[
          {
            id: 'play-pause',
            label: isPlaying ? 'Pause' : 'Play from start',
            variant: 'primary',
            style: { width: '164px', justifyContent: 'center' },
            icon: isPlaying ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" />
                <rect x="14" y="5" width="4" height="14" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            ),
            onClick: () => {
              if (isPlaying) {
                dispatch(togglePlay());
              } else {
                dispatch(seekToSeconds(0));
                dispatch(setPlaying(true));
              }
            },
          },
          { label: 'Share clip', variant: 'ghost', onClick: () => setShareOpen(true) },
          { label: 'Download', variant: 'ghost', onClick: () => setDownloadOpen(true) },
        ]}
      />
      ── END HERO SECTION ──
      */}

      {/* ModeBar tabs — sit between hero and content rows */}
      <ModeBar
        categories={systemCategories}
        activeChips={activeChips}
        onToggleChip={toggleChip}
        loading={libraryLoading}
        onDeleteChip={(id) => {
          const chip = systemCategories.find((c) => c.id === id);
          setPendingDeleteSection({ id, label: chip?.label ?? id });
        }}
      />

      {/* Content Rows */}
      <div className={styles.rows}>
        {!libraryLoading && filteredRows.length === 0 && (
          <p className={styles.modeEmpty} role="status">
            No {intelligenceMode === 'image' ? 'image' : 'video'} collections to show yet.
          </p>
        )}
        {filteredRows
          .map((row) => {
          const isFiltered = !activeChips.includes('all');
          // Every collection with content defaults to open (see ContentRow —
          // it remembers each collection's collapse choice across refreshes,
          // starting fresh sign-ins all-open). Chip filtering and Image
          // Intelligence mode (photo shelves are often sparse) force it open
          // regardless of what's stored, without overwriting that choice.
          const forceExpanded = isFiltered || intelligenceMode === 'image';
          return (
            <ContentRow
              key={row.id}
              row={row}
              forceExpanded={forceExpanded}
              loading={libraryLoading}
              onDelete={row.isUserCustom
                ? () => setPendingDeleteSection({ id: row.id, label: row.title })
                : undefined
              }
            >
              {row.type === 'trending'
                ? (row.items as TrendingItem[]).map((item) => (
                    <TrendingTile
                      key={item.id}
                      item={item}
                      onDelete={() => setPendingDeleteAsset({
                        assetId: item.id,
                        assetTitle: item.title ?? item.id,
                      })}
                    />
                  ))
                : (row.items as MediaItem[]).map((item) => (
                    <MediaTile
                      key={item.id}
                      item={item}
                      onRemove={!row.isDynamic
                        ? () => setPendingRemoveAsset({
                            assetId: item.id,
                            assetTitle: (item as MediaItem).title ?? item.id,
                            sectionId: row.id,
                            sectionTitle: row.title,
                          })
                        : undefined
                      }
                      onDelete={() => setPendingDeleteAsset({
                        assetId: item.id,
                        assetTitle: (item as MediaItem).title ?? item.id,
                      })}
                    />
                  ))}
            </ContentRow>
          );
})}
      </div>

      {/* Share modal */}
      {shareOpen && (
        <ShareModal
          url={activeAsset?.storage_url ?? window.location.href}
          title={heroTitle}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Download modal */}
      {downloadOpen && (
        <DownloadModal
          url={activeAsset?.download_url ?? activeAsset?.storage_url ?? '/assets/real-value-of-ai.mp4'}
          fileName={activeAsset?.file_name ?? 'real-value-of-ai.mp4'}
          fileSizeMb={activeAsset?.file_size_mb}
          durationMs={activeAsset?.duration_ms}
          onClose={() => setDownloadOpen(false)}
        />
      )}

      {/* Confirm: delete section */}
      {pendingDeleteSection && (
        <ConfirmDialog
          title="Delete collection?"
          message={<>Are you sure you want to delete <strong>{pendingDeleteSection.label}</strong>? This cannot be undone.</>}
          confirmLabel="Delete"
          onConfirm={() => {
            dispatch(deleteSection(pendingDeleteSection.id));
            // Remove from active chips if selected
            setActiveChips((prev) => {
              const next = prev.filter((c) => c !== pendingDeleteSection.id);
              return next.length === 0 ? ['all'] : next;
            });
            setPendingDeleteSection(null);
          }}
          onCancel={() => setPendingDeleteSection(null)}
        />
      )}

      {/* Confirm: remove asset from collection */}
      {pendingRemoveAsset && (
        <ConfirmDialog
          title="Remove from collection?"
          message={<>Remove <strong>{pendingRemoveAsset.assetTitle}</strong> from <strong>{pendingRemoveAsset.sectionTitle}</strong>?</>}
          confirmLabel="Remove"
          onConfirm={() => {
            dispatch(removeAssetFromSection({ assetId: pendingRemoveAsset.assetId, sectionId: pendingRemoveAsset.sectionId }));
            setPendingRemoveAsset(null);
          }}
          onCancel={() => setPendingRemoveAsset(null)}
        />
      )}

      {/* Confirm: permanently delete the asset (type-to-confirm) */}
      {pendingDeleteAsset && (
        <ConfirmDialog
          title="Delete video?"
          message={<>Permanently delete <strong>{pendingDeleteAsset.assetTitle}</strong>? This will remove it from all collections and cannot be undone.</>}
          confirmLabel="Delete"
          requireConfirmText="delete"
          onConfirm={() => {
            dispatch(deleteAsset(pendingDeleteAsset.assetId));
            setPendingDeleteAsset(null);
          }}
          onCancel={() => setPendingDeleteAsset(null)}
        />
      )}
    </div>
  );
}
